import React from 'react';
import { RefreshCw } from 'lucide-react';
import StatStrip from '../../common/StatStrip';
import Card from '../../common/Card';
import Button from '../../common/Button';
import Badge from '../../common/Badge';
import { STATUS_TONE } from '../parts/tones';

// Extracted verbatim from JobManagement's dashboard tab block (Task 7),
// restyled to B2 primitives (Task 7 of the mechanic UI restyle).
// Presentational only — `loadDashboardData` comes from useJobDashboard
// via the orchestrator; this tab calls no API itself.
//
// StatStrip has no icon slot, so the four coloured icon tiles the old
// StatCard grid rendered are dropped — a deliberate, spec-recorded change,
// not a regression.
//
// StatStrip sets grid-template-columns via inline `style`, which beats any
// grid-cols-N utility applied to it from outside (inline style always wins
// over a class). So responsive wrapping is done by swapping between one
// 4-stat strip (>=sm) and two 2-stat strips (<sm) rather than by trying to
// override StatStrip's own columns — StatStrip itself is not modified.
const DashboardTab = ({ stats, isLoading, loadDashboardData }) => {
  const statItems = stats && [
    { label: 'This Month', value: stats.jobs_this_month ?? 0 },
    { label: 'Overdue Jobs', value: stats.overdue_jobs ?? 0 },
    // Estimates the shop has not yet converted to invoices.
    { label: 'Pending Estimates', value: stats.pending_estimates ?? 0 },
    { label: 'Unpaid Invoices', value: stats.pending_invoices ?? 0 },
  ];

  return (
    <div className="space-y-8">
      {/* Refresh Button */}
      <div className="flex justify-end">
        <Button variant="secondary" icon={RefreshCw} loading={isLoading} onClick={loadDashboardData}>
          Refresh
        </Button>
      </div>

      {/* Stats Strip */}
      {statItems && (
        <>
          <div className="hidden sm:block">
            <StatStrip stats={statItems} />
          </div>
          <div className="sm:hidden space-y-3">
            <StatStrip stats={statItems.slice(0, 2)} />
            <StatStrip stats={statItems.slice(2, 4)} />
          </div>
        </>
      )}

      {/* Jobs by Status */}
      {stats?.jobs_by_status && Object.keys(stats.jobs_by_status).length > 0 && (
        <Card>
          <h3 className="text-card-title font-display text-ink mb-4">Jobs by Status</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(stats.jobs_by_status).map(([status, count]) => (
              <div key={status} className="text-center">
                <Badge status={STATUS_TONE[status] || 'neutral'}>
                  {status.replace(/_/g, ' ')}
                </Badge>
                <p className="font-mono tabular-nums text-page-title text-ink mt-2">{count}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
          <span className="ml-3 text-ink-muted">Loading dashboard...</span>
        </div>
      )}
    </div>
  );
};

export default DashboardTab;
